import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Popper,
  type PopperProps,
  Stack,
  Typography,
  FilterOptionsState,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useFormContext } from 'react-hook-form';
import { useIkpuInfo, useIkpusInfo } from '@/hooks/useIkpusInfo';
import { IkpuInfo } from '@/api/domains/search.types';
import { CustomListBox } from './CustomListBox';
import { isNumeric } from '@/helpers/string.helpers';

export type IkpuFieldProps = Omit<
  AutocompleteProps<IkpuOption, false, false, false>,
  'freeSolo' | 'autoComplete' | 'includeInputInList' | 'filterSelectedOptions' | 'loading' | 'noOptionsText' | 'options'
> & {
  fieldPrefix?: string;
  onIkpuInfoResolved?: (info: IkpuInfo | null) => void;
};

const WiderPopper = styled((props: PopperProps) => <Popper {...props} />)(({ theme }) => ({
  width: '600px !important',
  zIndex: theme.zIndex.modal,
}));

const StyledOption = styled('li', {
  shouldForwardProp: prop => prop !== 'isLast',
})<{ isLast?: boolean }>(({ theme, isLast }) => ({
  borderBottom: isLast ? 'none' : theme.shape.divider,
  borderRadius: '0 !important',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

type IkpuOption = IkpuInfo & { __isSearch?: true };

const getFilteredOptions = (options: IkpuOption[], params: FilterOptionsState<IkpuOption>) => {
  const { inputValue } = params;
  const normalizedInput = inputValue.trim().toLowerCase();

  const filtered = options.filter(
    option =>
      option.ikpu.toLowerCase().includes(normalizedInput) || option.drug_name.toLowerCase().includes(normalizedInput),
  );

  const isExisting = options.some(option => inputValue === option.ikpu);

  if (inputValue && isNumeric(inputValue) && !isExisting) {
    const commonMockData = {
      barcode: '',
      drug_name: '',
      maker_name: '',
      has_marks: false,
      package_code: 0,
      package_name: '',
    };

    filtered.push({
      ...commonMockData,
      ikpu: inputValue,
      __isSearch: true,
    });
  }

  return filtered;
};

const renderIkpuOption = (props: React.HTMLAttributes<HTMLLIElement>, option: IkpuOption) => (
  <StyledOption {...props} isLast={option.__isSearch}>
    {option.__isSearch ? (
      <Box py={1} px={1.5}>
        <Typography variant="Body/medium">Добавить</Typography>{' '}
        <Typography variant="Body/Bold/medium">{option.ikpu}</Typography>
      </Box>
    ) : (
      <Stack spacing={2} py={1} px={1.5}>
        <Box>
          <Typography variant="Body/Bold/medium">{option.drug_name}</Typography>
          <Typography variant="Body/small" color="neutral.700" component="p">
            {option.maker_name}
          </Typography>
        </Box>
        <Box>
          <Typography variant="Body/medium">Код ИКПУ: {option.ikpu}</Typography>
          <Typography variant="Body/small" color="neutral.700" component="p">
            Штрих-код: {option.barcode}
          </Typography>
        </Box>
      </Stack>
    )}
  </StyledOption>
);

export const IkpuField = (props: IkpuFieldProps) => {
  const { fieldPrefix, onIkpuInfoResolved, ...restProps } = props;
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [customIkpu, setCustomIkpu] = useState('');

  const { setValue, setError } = useFormContext();

  const ikpuFieldName = fieldPrefix ? `${fieldPrefix}.ikpu` : 'ikpu';
  const ikpuNameFieldName = fieldPrefix ? `${fieldPrefix}.ikpu_name` : undefined;
  const hasMarksFieldName = fieldPrefix ? `${fieldPrefix}.has_marks` : 'has_marks';
  const packageCodeFieldName = fieldPrefix ? `${fieldPrefix}.package_code` : 'package_code';

  useDebounce(() => setSearchQuery(inputValue), 500, [inputValue]);

  const { isFetching, ikpusInfo } = useIkpusInfo(
    {
      query: searchQuery,
    },
    {
      enabled: searchQuery.length > 2,
    },
  );

  const { isFetching: isFetchingCustomIkpu, ikpuInfo } = useIkpuInfo(
    {
      ikpu: customIkpu,
    },
    {
      enabled: !!customIkpu,
    },
  );

  const handleInputChange = (_event: React.SyntheticEvent, inputValue: string) => {
    setInputValue(inputValue);
  };

  const setIkpuName = (name: string) => {
    if (ikpuNameFieldName) {
      setValue(ikpuNameFieldName, name, { shouldDirty: true });
    }
  };

  useEffect(() => {
    if (customIkpu && !isFetchingCustomIkpu) {
      if (ikpuInfo) {
        setValue(hasMarksFieldName, ikpuInfo.has_marks, { shouldDirty: true });
        setValue(packageCodeFieldName, `${ikpuInfo.package_code} - ${ikpuInfo.package_name}`, { shouldDirty: true });
        setIkpuName(ikpuInfo.drug_name);
        onIkpuInfoResolved?.(ikpuInfo);
      } else {
        setError(ikpuFieldName, { message: 'Нет данных по этому коду' });
      }
    }
  }, [customIkpu, isFetchingCustomIkpu, ikpuInfo]);

  const handleChange = (_: unknown, value: IkpuOption | null) => {
    if (!value) {
      setValue(ikpuFieldName, '', { shouldValidate: true, shouldDirty: true });
      setValue(hasMarksFieldName, false, { shouldDirty: true });
      setValue(packageCodeFieldName, '', { shouldDirty: true });
      setIkpuName('');
      setCustomIkpu('');
      onIkpuInfoResolved?.(null);
      return;
    }

    setValue(ikpuFieldName, value.ikpu, { shouldValidate: true, shouldDirty: true });

    if (value.__isSearch && value.ikpu.length === 17) {
      setCustomIkpu(value.ikpu);
    } else {
      setValue(hasMarksFieldName, value.has_marks, { shouldDirty: true });
      if (value.package_code)
        setValue(packageCodeFieldName, `${value.package_code} - ${value.package_name}`, { shouldDirty: true });
      setIkpuName(value.drug_name);
      onIkpuInfoResolved?.(value);
    }
  };

  return (
    <Autocomplete
      {...restProps}
      onChange={handleChange}
      autoComplete
      includeInputInList
      filterSelectedOptions
      options={ikpusInfo}
      filterOptions={getFilteredOptions}
      handleHomeEndKeys
      PopperComponent={WiderPopper}
      ListboxComponent={props => <CustomListBox {...props} loading={isFetching} searchQuery={searchQuery} />}
      getOptionLabel={option => (typeof option === 'string' ? option : option.ikpu)}
      isOptionEqualToValue={(option, value) => option.ikpu === value.ikpu}
      onInputChange={handleInputChange}
      renderOption={renderIkpuOption}
    />
  );
};
